import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Pagination,
  Stack,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import { differenceInCalendarDays, parse } from 'date-fns';
import { cs } from 'date-fns/locale';
import React from 'react';
import { FileAttachment04, Pencil02, PlusCircle, Trash01 } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { useNotify } from '../hooks';
import { formatPrice } from '../utils/formatPrice';
import { AccountingDocumentsFilters } from './accountingDocuments/AccountingDocumentsFilters';
import { NewCashReceiptDialog, mapAccountingToCashReceipt } from './accountingDocuments/NewCashReceiptDialog';
import { useAccountingDocumets } from './accountingDocuments/hooks/useAccountingDocumets';
import { AccountingDocument } from './accountingDocuments/type/accountingDocuments';
import { PageLayout } from './common';
import { NoDataFound } from './common/NoDataFound';
import { RemoveCommonDialog } from './common/RemoveCommonDialog';
import { useAuth } from './hooks';
import { useAccountingDocumentsFilter } from './hooks/useAccountingDocumentsFilter';
import { Role } from './types';
import { RegistryCodeNote } from './types/accident';

interface CashReceiptDocumentProps {
  document: AccountingDocument;
  documentCodes?: RegistryCodeNote[];
  onClick?: () => void;
  onRemove?: (documentId: number) => void;
}
const CashReceiptDocument = ({ document, documentCodes, onClick, onRemove }: CashReceiptDocumentProps) => {
  const dueDateParsed = parse(document.dueDate, 'dd.MM.yyyy', new Date(), {
    locale: cs
  });
  const now = new Date();

  const isAfterDue = dueDateParsed < now;
  const daysAfterDue = isAfterDue ? differenceInCalendarDays(now, dueDateParsed) : 0;

  return (
    <Grid container width={1} spacing={2}>
      <Grid size={{ xs: 10, md: 3 }}>
        <Typography fontWeight="700">{document.documentNumber}</Typography>
        <Typography variant="body2">
          {documentCodes?.find((code) => code.code === document.documentCode)?.note}
        </Typography>
        {document.pdf && (
          <Box mt={1}>
            <Button
              variant="outlined"
              color="info"
              size="small"
              href={document.pdf}
              target="_blank"
              startIcon={<FileAttachment04 size="14px" />}
            >
              Stáhnout PDF
            </Button>
          </Box>
        )}
      </Grid>
      <Grid size={2} display={{ md: 'none' }}>
        <Stack direction="row" gap={1} justifyContent="end">
          <IconButton onClick={onClick} color="info" disabled={document.moneyS3Info}>
            <Pencil02 />
          </IconButton>
          <IconButton color="error" onClick={() => onRemove?.(document.id)}>
            <Trash01 />
          </IconButton>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 2 }}>
        {document.customer?.companyIdNum ? (
          <>
            <Typography fontWeight="700">{document.customer?.companyName}</Typography>
            <Typography variant="body2">IČO: {document.customer?.companyIdNum}</Typography>
            <Typography variant="body2">DIČ: {document.customer?.companyTaxNum}</Typography>
          </>
        ) : (
          <>
            <Typography fontWeight="700">
              {[document.customer?.name, document.customer?.surname].filter(Boolean).join(' ')}
            </Typography>
            <Typography variant="body2">RČ: {document.customer?.birthNumber}</Typography>
          </>
        )}
      </Grid>

      <Grid size={{ xs: 6, md: 3 }}>
        {isAfterDue && (
          <Typography color="error" fontWeight="700">
            🛑 Po splatnosti ({daysAfterDue} dní)
          </Typography>
        )}
        {document.dueDate && <Typography fontWeight="700">{document.dueDate}</Typography>}
        <Typography variant="body2">
          Datum vystavení: <br /> {document.issueDate}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6, md: 2 }}>
        <Typography fontWeight="700">{formatPrice(Number.parseFloat(document.totalPrice))}</Typography>
        <Typography variant="body2">vč. DPH</Typography>
      </Grid>

      <Grid size={2} display={{ xs: 'none', md: 'block' }}>
        <Stack direction="row" gap={1} justifyContent="end">
          <IconButton onClick={onClick} color="info" disabled={document.moneyS3Info}>
            <Pencil02 />
          </IconButton>
          <IconButton color="error" onClick={() => onRemove?.(document.id)}>
            <Trash01 />
          </IconButton>
        </Stack>
      </Grid>
    </Grid>
  );
};

type CashReceiptsPageProps = {
  documents?: AccountingDocument[];
  isLoading?: boolean;
};
const CashReceiptsPageContent = ({ documents, isLoading }: CashReceiptsPageProps) => {
  const { user } = useAuth();
  const { notify } = useNotify();
  const {
    isCreatingCashReceipt,
    createCashReceipt,
    editingDocument,
    editDocument,
    setEditingDocument,
    isEditing,
    handleRemoveDocument,
    openRemoveDocumentIdDialog,
    setOpenNewDialog,
    setOpenRemoveDocumentIdDialog,
    openNewDialog,
    isRemoving,
    startEdit,
    handleClose,
    documentCodes,
    isLoadingDocumentTypes,
    accountingCentres,
    isLoadingInvoices,
    accountingDocumentsInvoices
  } = useAccountingDocumets();

  const {
    items: accountingDocumentsFilteredValues,
    pagination,
    filters
  } = useAccountingDocumentsFilter(documents ?? [], {
    defaultPaginationState: { pageSize: 10, page: 1 }
  });

  const { data: accidentsData, isLoading: isLoadingAccidents } = useQuery({
    ...useCommonApi().getAccidents({ limit: 1000, offset: 0 }),
    enabled: openNewDialog
  });
  const accidents = accidentsData?.items;

  return (
    <PageLayout
      title="Pokladní doklady"
      extra={
        user?.role === Role.Dealer && (
          <Button
            variant="contained"
            color="success"
            startIcon={<PlusCircle size="20px" />}
            onClick={() => setOpenNewDialog(true)}
            size="large"
          >
            Přidat nový
          </Button>
        )
      }
    >
      {isLoading || isLoadingDocumentTypes ? (
        <Stack width={1} alignItems="center">
          <CircularProgress color="primary" />
        </Stack>
      ) : (
        <Stack spacing={2}>
          <AccountingDocumentsFilters filters={filters} disabled={isLoading} />
          <List
            sx={{
              width: 1,
              bgcolor: 'background.paper',
              paddingX: 1
            }}
          >
            {accountingDocumentsFilteredValues?.length ? (
              accountingDocumentsFilteredValues?.map((document, idx) => (
                <React.Fragment key={document.id}>
                  <ListItem
                    sx={{
                      paddingX: 1,
                      paddingY: 2,
                      width: 1
                    }}
                  >
                    <CashReceiptDocument
                      document={document}
                      documentCodes={documentCodes}
                      onRemove={(documentId) => setOpenRemoveDocumentIdDialog(documentId)}
                      onClick={() => startEdit(document)}
                    />
                  </ListItem>
                  {idx < accountingDocumentsFilteredValues.length - 1 && <Divider />}
                </React.Fragment>
              ))
            ) : (
              <NoDataFound />
            )}
          </List>
          {pagination.totalPages > 1 && (
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              variant="outlined"
              shape="rounded"
              onChange={(_, page) => pagination.setPage(page)}
            />
          )}
          {/* Add new Dialog */}
          <NewCashReceiptDialog
            open={openNewDialog}
            onClose={handleClose}
            accountingCentres={accountingCentres}
            documentCodes={documentCodes}
            isLoading={isCreatingCashReceipt}
            accidents={accidents}
            isLoadingAccidents={isLoadingAccidents || isEditing}
            defaultData={editingDocument ? mapAccountingToCashReceipt(editingDocument) : undefined}
            invoices={accountingDocumentsInvoices}
            invoicesLoading={isLoadingInvoices}
            onSubmit={(formData) => {
              if (editingDocument) {
                // EDIT
                editDocument(
                  { documentId: editingDocument.id, formData },
                  {
                    onSuccess: onSuccessCustomApiResponse({
                      executed: () => {
                        setOpenNewDialog(false);
                        setEditingDocument(null);
                      },
                      failed: (_, __, options) => {
                        if (options.statusCode === 439) {
                          setOpenNewDialog(false);
                          notify('success', 'Dokument byl založený, ale nebyl vygenerovaný PDF dokument');
                        }
                      }
                    })
                  }
                );
              } else {
                // CREATE
                createCashReceipt(
                  { formData },
                  {
                    onSuccess: onSuccessCustomApiResponse({
                      executed: () => setOpenNewDialog(false),
                      failed: (_, __, options) => {
                        if (options.statusCode === 439) {
                          setOpenNewDialog(false);
                          notify('success', 'Dokument byl založený, ale nebyl vygenerovaný PDF dokument');
                        }
                      }
                    })
                  }
                );
              }
            }}
          />
          <RemoveCommonDialog
            title="Pokladní doklad"
            onRemove={handleRemoveDocument}
            open={!!openRemoveDocumentIdDialog}
            onClose={() => setOpenRemoveDocumentIdDialog(null)}
            isLoading={isRemoving}
          />
        </Stack>
      )}
    </PageLayout>
  );
};

export const CashReceiptsPage = () => {
  const { user } = useAuth();

  const { data: cashReceipts, isLoading } = useQuery({
    ...useCommonApi().getCashReceipts(),
    enabled: user?.role === Role.Dealer
  });

  if (!user) return null;

  return <CashReceiptsPageContent documents={cashReceipts} isLoading={isLoading} />;
};
